import { useSearchParams } from "react-router-dom";
import { AuthService } from "../../../common/services/auth";
import { changeLanguage, localize } from "../../../common/services/localization";
import { EditProfileIcon, LanguageIcon, LogoutIcon, PaginationIcon } from "../ui/icons"
import { useEffect, useMemo, useState } from "react";

const authService = new AuthService();

export const HomeButton: React.FC<{
    className?: string;
}> = ({ className }) => {
    return <a
        href="/"
        className="hover:brightness-[110%]"
    >
        <img
            src="/images/logo-full.svg"
            className="w-full h-full object-contain"
            alt="Logo"
        />
    </a>
}

export const ProfileButton: React.FC<{
    className?: string;
    playerId: number;
}> = ({ playerId, className }) => {
    return (
        <button
            onClick={() => (window.location.href = `/players/${playerId}`)}
            className={`relative group w-10 h-10 ${className}`}
        >
            <img
                src="/images/profile.png"
                className="absolute inset-0 group-hover:hidden w-full h-full"
                alt="Profile"
            />
            <img
                src="/images/profile-hover.png"
                className="absolute inset-0 hidden group-hover:block w-full h-full"
                alt="Profile Hover"
            />
        </button>
    );
};

export const LogoutButton: React.FC<{
    className?: string;
}> = ({ className }) => {
    return (
        <button
            className={`brightness-[60%] hover:brightness-[150%] ${className}`}
            onClick={() => authService.logout()}>
            <LogoutIcon />
        </button>
    );
}

export const EditProfileButton: React.FC<{
    className?: string;
    onClick?: () => void
}> = ({ className, onClick }) => {
    return (
        <button
            className={`brightness-[100%] hover:brightness-[500%] ${className}`}
            onClick={onClick}>
            <EditProfileIcon />
        </button>
    );
}

export const LocalizationsButton: React.FC<{
    className?: string
}> = ({ className }) => {
    return <button
        className={` ${className ?? ''}`}
        onClick={() => changeLanguage()}>
        <LanguageIcon />
    </button>
}

export const AuthButton: React.FC<{
    className?: string
}> = ({ className }) => {

    return <button
        onClick={() => {
            authService.redirectSteamAuth()
        }}
        className={`${className} px-[14px] py-[8px] lg:px-6 lg:py-2.5 rounded-sm outline outline-[3px] outline-offset-[-3px] 
        outline-white/50 hover:outline-amber-500 inline-flex justify-center items-center gap-2.5 transition-colors`
        }>
        <div className="justify-start text-white text-[14px] lg:text-lg font-semibold font-['Reaver'] 
        uppercase [text-shadow:_0px_1px_4px_rgb(0_0_0_/_0.57)]" >
            {localize('btn_buy_login')}
        </div>
        < div className="w-4 lg:w-6 lg:h-6 relative overflow-hidden" >
            <img src="/images/steam-white-logo.svg" />
        </div>
    </button>
}

export const BuyShardsButton: React.FC<{
    className?: string
    textClassName?: string
    isAuthorized?: boolean
}> = ({ className, textClassName, isAuthorized }) => {
    const [params, setParams] = useSearchParams();

    return <OrangeButton
        textClassName={textClassName}
        localText="btn_buy_shards"
        className={`px-6 py-2.5 text-18 ${className}`}
        onClick={() => {
            if (isAuthorized) {
                params.append('popup', 'shards' as PopupType);
                setParams(params);
            } else {
                authService.redirectSteamAuth()
            }
        }}
    />;
}

export const SubFooterButton: React.FC<{
    className?: string
    textClassName?: string
    isAuthorized?: boolean
}> = ({ className, textClassName, isAuthorized }) => {
    const [params, setParams] = useSearchParams();

    return <OrangeButton
        localText="btn_sub"
        textClassName={textClassName}
        className={`text-[14px] px-[14px] py-[12px] lg:w-[300px] lg:px-[38px] lg:h-[76px] lg:text-28 ${className}`}
        onClick={() => {
            if (isAuthorized) {
                params.append('popup', 'dota_plus' as PopupType);
                setParams(params);
            } else {
                authService.redirectSteamAuth()
            }
        }}
    />;
}

export const SubButton: React.FC<{
    className?: string
    localText?: string
    isAuthorized?: boolean
}> = ({ className, localText, isAuthorized }) => {
    const [params, setParams] = useSearchParams();

    return <OrangeButton
        localText={localText || 'btn_sub'}
        className={`px-4 py-2 md:px-8 md:py-4 text-[14px] md:text-24 font-semibold  ${className}`}
        onClick={() => {
            if (isAuthorized) {
                params.append('popup', 'dota_plus' as PopupType);
                setParams(params);
            } else {
                authService.redirectSteamAuth()
            }
        }}
    />;
}

export const OrangeButton: React.FC<{
    className?: string
    textClassName?: string
    onClick?: any
    localText: string
    disabled?: boolean
}> = ({ onClick, className, textClassName, localText, disabled }) => {
    return <button
        disabled={disabled}
        onClick={onClick}
        className={`font-semibold font-['Reaver'] bg-gradient-to-l from-amber-500 to-orange-500 
        rounded-sm shadow-[0px_0px_3.6px_0px_rgba(208,113,30,1)] 
        inline-flex justify-center items-center gap-2.5 
        transition-all duration-300 hover:bg-gradient-to-l 
        hover:from-amber-400 hover:to-orange-400 hover:shadow-[0px_0px_3.6px_0px_rgba(208,113,30,0.75)]
        ${className ?? ''}`
        }>
        <div className={`justify-start text-white uppercase ${textClassName || ''}`} >
            {localize(localText)}
        </div>
    </button>
}

export const BlueButton: React.FC<{
    className?: string
    textClassName?: string
    onClick?: any
    localText: string
    disabled?: boolean
}> = ({ onClick, className, localText, textClassName, disabled }) => {
    return (
        <button
            disabled={disabled}
            onClick={onClick}
            className={` px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 
            rounded-sm shadow-[0px_0px_3.6px_0px_rgba(32,88,158,1)] inline-flex justify-center 
            items-center gap-2.5 transition-all duration-300 hover:bg-gradient-to-r 
            hover:from-blue-500 hover:to-blue-500 hover:shadow-[0px_0px_3.6px_0px_rgba(32,88,158,0.5)] 
            ${className ?? ''}`
            }
        >
            <div className={`justify-start text-white font-semibold font-['Reaver'] uppercase ${textClassName ?? 'text-[18px]'}`} >
                {localize(localText)}
            </div>
        </button>
    );
}

export const SendPresentButton: React.FC<{
    className?: string
    textClassName?: string
    isAuthorized?: boolean
}> = ({ className, textClassName, isAuthorized }) => {
    const [searchParams, setSearchParams] = useSearchParams();

    return <BlueButton localText="btn_gift_send"
        textClassName={textClassName}
        className={className}
        onClick={() => {
            if (isAuthorized) {
                searchParams.append('popup', 'present' as PopupType);
                setSearchParams(searchParams);
            } else {
                authService.redirectSteamAuth()
            }
        }}
    />;
}

export const PaginationButtons = <T,>({
    onClick,
    className,
    response,
    currentPage,
}: PaginationProps<T>) => {
    const totalPages = response.data.totalPages;

    const handlePrev = () => {
        if (onClick && currentPage > 1) {
            onClick(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (onClick && currentPage < totalPages) {
            onClick(currentPage + 1);
        }
    };

    const generatePageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;
        let start = Math.max(currentPage - 2, 1);
        let end = Math.min(start + maxPagesToShow - 1, totalPages);

        if (end - start < maxPagesToShow - 1) {
            start = Math.max(end - maxPagesToShow + 1, 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return pages;
    };

    const pages = generatePageNumbers();

    return (
        <div className={`flex flex-row items-center text-[12px] lg:text-16 font-['Roboto'] gap-[6px] lg:gap-4 ${className ?? ''}`}>
            <PaginationMoveButton
                isNext={false}
                onClick={handlePrev}
                disabled={currentPage <= 1}
            />

            <div className="flex items-center gap-2">
                {pages[0] > 1 && (
                    <>
                        <PageButton page={1} onClick={onClick} isActive={1 === currentPage} />
                        {pages[0] > 2 && <span>...</span>}
                    </>
                )}
                {pages.map((page) => (
                    <PageButton key={page} page={page} onClick={onClick} isActive={page === currentPage} />
                ))}
                {pages[pages.length - 1] < totalPages && (
                    <>
                        {pages[pages.length - 1] < totalPages - 1 && <span>...</span>}
                        <PageButton page={totalPages} onClick={onClick} isActive={totalPages === currentPage} />
                    </>
                )}
            </div>

            <PaginationMoveButton
                isNext={true}
                onClick={handleNext}
                disabled={currentPage >= totalPages}
            />
        </div>
    );
};

const PageButton: React.FC<{
    page: number;
    onClick?: (page: number) => void;
    isActive: boolean;
}> = ({ page, onClick, isActive }) => {
    return (
        <button className={`lg:w-[48px] lg:h-[32px] px-[4px] py-[2px] lg:px-[10px] lg:py-[6px] flex items-center justify-center
                ${isActive ? 'text-white bg-orange' : 'bg-transparent text-orange hover:bg-orange/10'}`}
            onClick={() => onClick?.(page)}
        >
            {page}
        </button>
    );
};

const PaginationMoveButton: React.FC<{
    className?: string;
    isNext: boolean;
    onClick?: any;
    disabled: boolean;
}> = ({ onClick, className, isNext, disabled }) => {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-[2px] lg:gap-[4px] w-[120px] h-[22px] text-orange
                ${disabled ? 'opacity-40 pointer-events-none' : ''} ${className ?? ''}`}
        >
            {isNext
                ? (
                    <>
                        <span>{localize('pagination-btn-next')}</span>
                        <PaginationIcon className="w-[16px] h-[16px]" isNext={isNext} />
                    </>
                )
                : (
                    <>
                        <PaginationIcon className="w-[16px] h-[16px]" isNext={isNext} />
                        <span>{localize('pagination-btn-prev')}</span>
                    </>
                )}
        </button>
    );
};

export const ToggleSwitch: React.FC<{
    isOn?: boolean;
    onToggle?: (value: boolean) => void;
}> = ({ isOn = false, onToggle }) => {
    const [checked, setChecked] = useState(isOn);

    const toggle = () => {
        const newValue = !checked;
        setChecked(newValue);
        onToggle?.(newValue);
    };

    return (
        <div
            onClick={toggle}
            className={`w-[42px] h-[24px] rounded-full cursor-pointer relative transition-colors duration-300 ${checked ? 'bg-orange' : 'bg-[#314653]'
                }`}
        >
            <div
                className={`w-[16px] h-[16px] rounded-full absolute top-1 transition-all duration-300 ${checked ? 'left-[22px] bg-white' : 'left-[4px] bg-white'
                    }`}
            />
        </div>
    );
};

export const SwitchWithValuesBtn: React.FC<{
    isOn: boolean,
    handleToggle: any,
    classNames?: string
    leftValue: string,
    rigthValue: string
}> = props => {
    const isOn = props.isOn;

    return (
        <label className={`switch ${props.classNames}`}>
            <input
                checked={isOn}
                onChange={props.handleToggle}
                className="switch-checkbox"
                type="checkbox"
            />
            <div className="switch-button" />
            <div className="switch-labels">
                <span style={{ opacity: `${isOn ? 0.2 : 1}` }}
                >
                    {props.leftValue}
                </span>
                <span style={{ opacity: `${isOn ? 1 : 0.2}` }}
                >
                    {props.rigthValue}
                </span>
            </div>
        </label>
    );
}
